import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Command, Activity, Shield, Users, Layers } from "lucide-react";
import { Button } from "@vild/site-ui";

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  React.useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-mono font-bold text-xl tracking-tight text-white">
          <div className="w-6 h-6 bg-zinc-100 rounded-sm"></div>
          VILD
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          <div className="group relative">
            <button className="flex items-center gap-1 text-sm font-medium text-zinc-400 hover:text-white transition-colors">
              Features <ChevronDown className="w-3 h-3" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4 hidden group-hover:block w-[400px]">
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2 shadow-xl grid grid-cols-1 gap-1">
                <Link to="/features/runtime" className="flex items-center gap-3 p-3 hover:bg-zinc-800 rounded-md transition-colors group/item">
                  <Activity className="w-5 h-5 text-zinc-500 group-hover/item:text-white" />
                  <div>
                    <div className="text-sm font-medium text-zinc-200 group-hover/item:text-white">Runtime</div>
                    <div className="text-xs text-zinc-500">Execution, scaling, cost</div>
                  </div>
                </Link>
                <Link to="/features/gates" className="flex items-center gap-3 p-3 hover:bg-zinc-800 rounded-md transition-colors group/item">
                  <Shield className="w-5 h-5 text-zinc-500 group-hover/item:text-white" />
                  <div>
                    <div className="text-sm font-medium text-zinc-200 group-hover/item:text-white">Gates</div>
                    <div className="text-xs text-zinc-500">Human-in-the-loop approvals</div>
                  </div>
                </Link>
                <Link to="/features/composition" className="flex items-center gap-3 p-3 hover:bg-zinc-800 rounded-md transition-colors group/item">
                  <Layers className="w-5 h-5 text-zinc-500 group-hover/item:text-white" />
                  <div>
                    <div className="text-sm font-medium text-zinc-200 group-hover/item:text-white">Composition</div>
                    <div className="text-xs text-zinc-500">Nodes, modules, integrations</div>
                  </div>
                </Link>
                <Link to="/features/observability" className="flex items-center gap-3 p-3 hover:bg-zinc-800 rounded-md transition-colors group/item">
                  <Command className="w-5 h-5 text-zinc-500 group-hover/item:text-white" />
                  <div>
                    <div className="text-sm font-medium text-zinc-200 group-hover/item:text-white">Observability</div>
                    <div className="text-xs text-zinc-500">Activity, logs, debugging</div>
                  </div>
                </Link>
                <Link to="/features/collaboration" className="flex items-center gap-3 p-3 hover:bg-zinc-800 rounded-md transition-colors group/item">
                  <Users className="w-5 h-5 text-zinc-500 group-hover/item:text-white" />
                  <div>
                    <div className="text-sm font-medium text-zinc-200 group-hover/item:text-white">Collaboration</div>
                    <div className="text-xs text-zinc-500">Teams, permissions, sharing</div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          <Link to="/starters" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">
            Starters
          </Link>
          <Link to="/pricing" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">
            Pricing
          </Link>
          <Link to="/enterprise" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">
            Enterprise
          </Link>
          <Link to="/docs" className="text-sm font-medium text-zinc-400 hover:text-white transition-colors">
            Docs
          </Link>
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/login" className="text-sm font-medium text-zinc-300 hover:text-white transition-colors">
            Sign In
          </Link>
          <Button asLink="/signup">
            Get Started
          </Button>
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-zinc-400 hover:text-white">
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden border-t border-zinc-800 bg-zinc-950 p-6 flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <div className="text-xs font-mono text-zinc-500 uppercase">Features</div>
            <Link to="/features/runtime" className="text-zinc-300 hover:text-white">
              Runtime
            </Link>
            <Link to="/features/gates" className="text-zinc-300 hover:text-white">
              Gates
            </Link>
            <Link to="/features/composition" className="text-zinc-300 hover:text-white">
              Composition
            </Link>
            <Link to="/features/observability" className="text-zinc-300 hover:text-white">
              Observability
            </Link>
            <Link to="/features/collaboration" className="text-zinc-300 hover:text-white">
              Collaboration
            </Link>
          </div>
          <div className="h-px bg-zinc-800"></div>
          <Link to="/starters" className="text-lg font-medium text-white">
            Starters
          </Link>
          <Link to="/pricing" className="text-lg font-medium text-white">
            Pricing
          </Link>
          <Link to="/enterprise" className="text-lg font-medium text-white">
            Enterprise
          </Link>
          <Link to="/docs" className="text-lg font-medium text-white">
            Docs
          </Link>
          <div className="h-px bg-zinc-800"></div>
          <div className="flex flex-col gap-3">
            <Link to="/login" className="text-zinc-300 hover:text-white text-center py-2 transition-colors">
              Sign In
            </Link>
            <Button className="w-full" asLink="/signup">
              Get Started
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer: React.FC = () => (
  <footer className="border-t border-zinc-800 bg-zinc-950 py-16 px-6">
    <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
      <div className="col-span-2 lg:col-span-2">
        <Link to="/" className="flex items-center gap-2 font-mono font-bold text-xl tracking-tight text-white mb-4">
          <div className="w-5 h-5 bg-zinc-100 rounded-sm"></div>
          VILD
        </Link>
        <p className="text-zinc-500 text-sm max-w-xs">
          The production runtime for media pipelines. Transform media, automatically.
        </p>
      </div>

      <div>
        <h4 className="font-mono text-sm font-semibold text-white mb-4">Product</h4>
        <ul className="space-y-2 text-sm text-zinc-500">
          <li>
            <Link to="/features/runtime" className="hover:text-white">
              Runtime
            </Link>
          </li>
          <li>
            <Link to="/features/gates" className="hover:text-white">
              Gates
            </Link>
          </li>
          <li>
            <Link to="/starters" className="hover:text-white">
              Starters
            </Link>
          </li>
          <li>
            <Link to="/pricing" className="hover:text-white">
              Pricing
            </Link>
          </li>
          <li>
            <Link to="/enterprise" className="hover:text-white">
              Enterprise
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-mono text-sm font-semibold text-white mb-4">Solutions</h4>
        <ul className="space-y-2 text-sm text-zinc-500">
          <li>
            <Link to="/for/agencies" className="hover:text-white">
              Agencies
            </Link>
          </li>
          <li>
            <Link to="/for/studios" className="hover:text-white">
              Studios
            </Link>
          </li>
          <li>
            <Link to="/for/marketing" className="hover:text-white">
              Marketing
            </Link>
          </li>
          <li>
            <Link to="/for/ecommerce" className="hover:text-white">
              E-commerce
            </Link>
          </li>
        </ul>
      </div>

      <div>
        <h4 className="font-mono text-sm font-semibold text-white mb-4">Company</h4>
        <ul className="space-y-2 text-sm text-zinc-500">
          <li>
            <Link to="/docs" className="hover:text-white">
              Documentation
            </Link>
          </li>
          <li>
            <Link to="/blog" className="hover:text-white">
              Blog
            </Link>
          </li>
          <li>
            <Link to="/contact" className="hover:text-white">
              Contact
            </Link>
          </li>
          <li>
            <a href="#" className="hover:text-white">
              Twitter
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-zinc-900 text-xs text-zinc-600 font-mono flex flex-col md:flex-row justify-between items-center gap-4">
      <div>&copy; 2026 Vild Inc.</div>
      <div className="flex gap-6">
        <a href="#" className="hover:text-zinc-400">
          Privacy
        </a>
        <a href="#" className="hover:text-zinc-400">
          Terms
        </a>
        <a href="#" className="hover:text-zinc-400">
          Status
        </a>
      </div>
    </div>
  </footer>
);

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-zinc-950 text-zinc-50 font-sans selection:bg-zinc-700 selection:text-white">
    <Navbar />
    <main className="pt-16">{children}</main>
    <Footer />
  </div>
);

